import * as THREE from 'three';
import { PF_HH, PF_HW } from '../sim/constants';
import { crystal } from './art/shapes';
import { Cell, DECOR_MAX_LUMINANCE, hexToRgb } from './palette';
import { Layer } from './RenderLayers';

/* ═══════════════════════════════════════════════════════════════════════════
   LE DÉCOR — la moitié de « reconnaissable sur une capture d'écran ».

   Une flotte, même bien dessinée, posée sur un fond noir uni ne produit pas une
   image identifiable : elle produit une planche de modèles. Ce qui rend une
   capture de shmup reconnaissable, c'est la GÉOGRAPHIE — le joueur doit voir
   qu'il descend dans quelque chose.

   Quatre couches, quatre draw calls, zéro octet téléchargé :

     1. Le CIEL (z = −1340) — dégradé froid et strates qui défilent lentement.
     2. La POUSSIÈRE — deux nappes de points en parallaxe. Elles donnent la
        vitesse, ce que ni le ciel ni les structures ne savent faire seuls.
     3. La TRANCHÉE — deux parois de blocs qui défilent hors du terrain. Elles
        occupent les marges latérales que §4.2 réserve au décor 3D, et ce sont
        elles qui transforment « des vaisseaux sur un fond » en « des vaisseaux
        DANS un endroit ».
     4. Le SCRIM (§4.5, couche 500) — un voile noir à 0,22 posé sur TOUT le
        décor. Sans lui, la luminance du fond finit par concurrencer celle des
        projectiles et la lecture s'effondre sans que rien ne le signale.

   ⚠️ Toute la géographie reste HORS du terrain de jeu : les parois sont à
   |x| ≥ 630 alors que le terrain s'arrête à 450, et rien n'y est collisionnable.
   Un décor qui empiète sur le playfield est la manière la plus rapide de rendre
   un shmup illisible — le joueur cesse de savoir ce qui est du sol et ce qui est
   une menace.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Vitesse de défilement de référence, en unités par seconde. Identique à celle
 *  que le point d'entrée applique aux débris d'explosion : les deux mondes
 *  doivent glisser ensemble, sinon les étincelles dérivent visiblement par
 *  rapport au décor (PLAN.md §4.7). */
export const SCROLL_SPEED = 190;

/** Hachage entier déterministe. Il fait varier les blocs de la tranchée en
 *  fonction de leur position ABSOLUE dans le monde, et non de leur emplacement
 *  dans le tampon : c'est ce qui rend le défilement infini sans couture. */
function hash01(i: number): number {
  let x = Math.imul(i ^ 0x9e3779b9, 0x85ebca6b);
  x ^= x >>> 13;
  x = Math.imul(x, 0xc2b2ae35);
  x ^= x >>> 16;
  return (x >>> 0) / 4294967296;
}

const SKY_VERT = /* glsl */ `
varying vec2 vXy;
void main() {
  vXy = position.xy;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;

const SKY_FRAG = /* glsl */ `
precision mediump float;
uniform vec3 uTop;
uniform vec3 uBottom;
uniform float uScroll;
uniform float uMaxLum;
varying vec2 vXy;

void main() {
  float v = clamp(vXy.y / 2400.0 + 0.5, 0.0, 1.0);
  vec3 col = mix(uBottom, uTop, v);

  // Strates horizontales qui descendent. Très basse amplitude : leur rôle est
  // de faire exister le mouvement en périphérie, pas de se faire remarquer.
  float band = sin((vXy.y + uScroll * 0.35) * 0.0075);
  col += vec3(0.012, 0.020, 0.032) * smoothstep(0.86, 1.0, band);

  // Vignettage horizontal : les bords s'assombrissent, ce qui recentre l'œil
  // sur le terrain sans avoir à y poser quoi que ce soit.
  col *= 1.0 - smoothstep(700.0, 2600.0, abs(vXy.x)) * 0.55;

  /* ⚠️ PLAFOND DE LUMINANCE (PLAN.md §4.5). Le décor ne dépasse jamais 45 %,
     et on l'impose ICI plutôt que de compter sur des constantes bien choisies :
     un dégradé est une machine à produire des valeurs intermédiaires, et
     personne ne vérifie à la main les 2 073 600 pixels du fond. */
  float lum = dot(col, vec3(0.2126, 0.7152, 0.0722));
  if (lum > uMaxLum) col *= uMaxLum / lum;

  gl_FragColor = vec4(col, 1.0);
}`;

const DUST_VERT = /* glsl */ `
attribute float aSize;
attribute float aParallax;
uniform float uScroll;
uniform float uSpan;
varying float vFade;

void main() {
  vec3 p = position;
  // Enroulement analytique : la poussière ne se recycle jamais côté CPU, aucun
  // tampon ne remonte au GPU. Le champ est infini pour le prix d'un modulo.
  p.y = mod(p.y - uScroll * aParallax + uSpan * 0.5, uSpan) - uSpan * 0.5;
  vec4 mv = modelViewMatrix * vec4(p, 1.0);
  // Les grains lointains sont plus ternes : c'est la profondeur, portée par la
  // valeur plutôt que par la taille, qui reste lisible à 1 pixel.
  vFade = aParallax;
  gl_Position = projectionMatrix * mv;
  gl_PointSize = aSize;
}`;

const DUST_FRAG = /* glsl */ `
precision mediump float;
uniform vec3 uColor;
varying float vFade;
void main() {
  gl_FragColor = vec4(uColor * (0.35 + vFade * 0.65), 0.55 * vFade);
}`;

export class Backdrop {
  private readonly skyUniforms: { uScroll: { value: number } };
  private readonly dustUniforms: { uScroll: { value: number } };
  private readonly trench: THREE.InstancedMesh;
  private readonly grid: THREE.LineSegments;

  /** Nombre de blocs par colonne et par nappe. */
  private static readonly SLABS = 13;
  /** Pas vertical entre deux blocs. */
  private static readonly SLAB_STEP = 270;
  /** Colonnes : deux côtés × deux profondeurs. */
  /** ⚠️ Les Z sont contraints par la position du scrim (−180). Un bloc dont
   *  l'arête faîtière dépasserait cette profondeur percerait le voile et
   *  s'afficherait à pleine luminance au milieu d'un décor assombri — une tache
   *  claire en périphérie, exactement ce que le plafond de luminance interdit.
   *  L'échelle en Z des blocs est bornée en conséquence dans `layout`. */
  private static readonly COLUMNS: readonly { x: number; z: number; par: number; tint: number }[] = [
    { x: -640, z: -420, par: 1.0, tint: 1.0 },
    { x: 640, z: -420, par: 1.0, tint: 1.0 },
    { x: -1080, z: -900, par: 0.62, tint: 0.55 },
    { x: 1080, z: -900, par: 0.62, tint: 0.55 },
  ];

  private readonly _m = new THREE.Matrix4();
  private readonly _p = new THREE.Vector3();
  private readonly _q = new THREE.Quaternion();
  private readonly _s = new THREE.Vector3();

  constructor(scene: THREE.Scene, hullMaterial: THREE.Material) {
    /* ── 1. Le ciel ────────────────────────────────────────────────────── */
    const far = hexToRgb('#0A1826');
    const near = hexToRgb('#050810');
    this.skyUniforms = { uScroll: { value: 0 } };
    const skyMat = new THREE.ShaderMaterial({
      vertexShader: SKY_VERT,
      fragmentShader: SKY_FRAG,
      uniforms: {
        uTop: { value: new THREE.Vector3(far[0], far[1], far[2]) },
        uBottom: { value: new THREE.Vector3(near[0], near[1], near[2]) },
        uMaxLum: { value: DECOR_MAX_LUMINANCE },
        ...this.skyUniforms,
      },
      depthWrite: false,
      depthTest: false,
    });
    skyMat.customProgramCacheKey = (): string => 'sky-v1';
    const sky = new THREE.Mesh(new THREE.PlaneGeometry(7200, 4800), skyMat);
    sky.position.z = -1340;
    sky.renderOrder = Layer.SkyFar;
    sky.frustumCulled = false;
    scene.add(sky);

    /* ── 2. La poussière ───────────────────────────────────────────────── */
    const COUNT = 900;
    const SPAN = 3600;
    const pos = new Float32Array(COUNT * 3);
    const size = new Float32Array(COUNT);
    const par = new Float32Array(COUNT);
    for (let i = 0; i < COUNT; i++) {
      pos[i * 3] = (hash01(i * 3 + 1) - 0.5) * 3400;
      pos[i * 3 + 1] = (hash01(i * 3 + 2) - 0.5) * SPAN;
      pos[i * 3 + 2] = -260 - hash01(i * 3 + 3) * 900;
      const depth = hash01(i * 7 + 11);
      par[i] = 0.35 + depth * 0.9;
      size[i] = 1 + depth * 2.2;
    }
    const dustGeo = new THREE.BufferGeometry();
    dustGeo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    dustGeo.setAttribute('aSize', new THREE.BufferAttribute(size, 1));
    dustGeo.setAttribute('aParallax', new THREE.BufferAttribute(par, 1));
    this.dustUniforms = { uScroll: { value: 0 } };
    const dustMat = new THREE.ShaderMaterial({
      vertexShader: DUST_VERT,
      fragmentShader: DUST_FRAG,
      uniforms: {
        uColor: { value: new THREE.Color(0x8fb6d8) },
        uSpan: { value: SPAN },
        ...this.dustUniforms,
      },
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    dustMat.customProgramCacheKey = (): string => 'dust-v1';
    const dust = new THREE.Points(dustGeo, dustMat);
    dust.frustumCulled = false;
    dust.renderOrder = Layer.SkyFar + 1;
    scene.add(dust);

    /* ── 3. La tranchée ────────────────────────────────────────────────── */
    // Un bloc : le même générateur de coques que la flotte, avec un contour
    // rectangulaire. C'est ce qui fait que le décor et les vaisseaux ont l'air
    // taillés dans la même matière — il n'existe littéralement qu'un seul
    // outil de découpe dans ce projet.
    const slab = crystal(
      [
        [-1, 0.5],
        [-1, -0.5],
        [1, -0.5],
        [1, 0.5],
      ],
      { ridge: 0.9, keel: 0.2, cell: Cell.DecorMid },
    );
    const slabGeo = new THREE.BufferGeometry();
    slabGeo.setAttribute('position', new THREE.Float32BufferAttribute(slab.position, 3));
    slabGeo.setAttribute('normal', new THREE.Float32BufferAttribute(slab.normal, 3));
    slabGeo.setAttribute('uv', new THREE.Float32BufferAttribute(slab.uv, 2));

    const total = Backdrop.COLUMNS.length * Backdrop.SLABS;
    this.trench = new THREE.InstancedMesh(slabGeo, hullMaterial, total);
    this.trench.frustumCulled = false;
    this.trench.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.trench.instanceColor = new THREE.InstancedBufferAttribute(
      new Float32Array(total * 3).fill(1),
      3,
    );
    this.trench.renderOrder = Layer.DecorMid;
    this.trench.count = total;
    scene.add(this.trench);

    /* ── 4. La grille de sol ───────────────────────────────────────────── */
    const pts: number[] = [];
    for (let x = -PF_HW; x <= PF_HW; x += 150) pts.push(x, -PF_HH * 2.4, -560, x, PF_HH * 2.4, -560);
    for (let y = -12; y <= 12; y++) {
      const yy = y * 150;
      pts.push(-PF_HW, yy, -560, PF_HW, yy, -560);
    }
    const gridGeo = new THREE.BufferGeometry();
    gridGeo.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
    this.grid = new THREE.LineSegments(
      gridGeo,
      new THREE.LineBasicMaterial({ color: 0x14263a, transparent: true, opacity: 0.5 }),
    );
    this.grid.frustumCulled = false;
    this.grid.renderOrder = Layer.DecorMid + 1;
    scene.add(this.grid);

    /* ── 5. Le scrim ───────────────────────────────────────────────────── */
    /* ⚠️ La couche 500 du plan, et elle est OBLIGATOIRE. C'est un voile noir à
       0,22 posé sur tout le décor et sous tout le gameplay : il écrase la
       luminance du fond d'un cran supplémentaire, après le plafond appliqué
       dans le shader de ciel, et sans toucher aux vaisseaux ni aux
       projectiles. Le décor peut donc être aussi détaillé qu'on veut sans
       jamais entrer en concurrence avec la couche danger.

       ⚠️ PIÈGE : `renderOrder` NE SUFFIT PAS À LE PLACER. three tient deux
       listes de rendu, opaque et transparente, et dessine TOUJOURS la totalité
       de la première avant la seconde ; `renderOrder` ne trie qu'à l'intérieur
       d'une liste. Un scrim transparent en couche 500 passe donc après les
       coques opaques de la couche 600, et assombrit toute la flotte de 22 %.
       Le résultat est parfaitement plausible — un jeu un peu terne — et rien
       n'indique que le voile est du mauvais côté.

       Ce qui le place réellement, c'est la PROFONDEUR. Le scrim est posé à
       z = −180, c'est-à-dire exactement sur la frontière que §4.2 assigne au
       décor : tout le décor est derrière, tout le jeu est devant. Avec
       `depthTest` actif, ses fragments sont rejetés partout où une coque a déjà
       écrit une profondeur plus proche, et acceptés partout ailleurs. Le voile
       tombe donc sur le décor et sur rien d'autre, sans dépendre d'un ordre.

       `depthWrite` reste désactivé : il n'a aucune raison d'occulter ce qui
       viendra après lui. */
    const scrim = new THREE.Mesh(
      new THREE.PlaneGeometry(7200, 4800),
      new THREE.MeshBasicMaterial({
        color: 0x000000,
        transparent: true,
        opacity: 0.22,
        depthTest: true,
        depthWrite: false,
      }),
    );
    scrim.position.z = -180;
    scrim.renderOrder = Layer.Scrim;
    scrim.frustumCulled = false;
    scene.add(scrim);

    this.layout(0);
  }

  /** `scroll` en unités monde, croissant. */
  update(scroll: number): void {
    this.skyUniforms.uScroll.value = scroll;
    this.dustUniforms.uScroll.value = scroll;
    this.grid.position.y = -(scroll % 150);
    this.layout(scroll);
  }

  /**
   * Repose les blocs de la tranchée.
   *
   * Cinquante-deux `compose` par frame, sans une seule allocation. On aurait pu
   * décaler les groupes en bloc pour deux écritures au lieu de cinquante-deux —
   * mais la variation de chaque bloc doit suivre sa position ABSOLUE dans le
   * monde, sinon le motif saute d'un cran chaque fois que le modulo repasse par
   * zéro. Un décor qui hoquette toutes les quelques secondes est un défaut que
   * l'on voit sans savoir le nommer.
   */
  private layout(scroll: number): void {
    const step = Backdrop.SLAB_STEP;
    const half = (Backdrop.SLABS * step) / 2;
    let n = 0;

    for (const col of Backdrop.COLUMNS) {
      const travel = scroll * col.par;
      const base = Math.floor(travel / step);
      const frac = travel - base * step;

      for (let i = 0; i < Backdrop.SLABS; i++) {
        // L'indice de MONDE, pas l'indice de tampon : c'est lui qui porte la
        // variation, et c'est ce qui rend le défilement sans couture.
        const world = base + i;
        const h1 = hash01(world * 2 + (col.x < 0 ? 7919 : 104729));
        const h2 = hash01(world * 2 + 1 + (col.x < 0 ? 7919 : 104729));

        const w = 90 + h1 * 210;
        const h = 60 + h2 * 130;
        this._p.set(
          col.x + (col.x < 0 ? -w * 0.5 : w * 0.5) + (h1 - 0.5) * 90,
          i * step - half - frac,
          col.z - h2 * 120,
        );
        this._q.identity();
        // Le facteur 0,35 en Z : l'arête faîtière du contour vaut 0,9 en unités
        // normalisées, donc un bloc de 300 unités de large avance de 0,9 × 0,35
        // × 300 ≈ 95 unités vers la caméra. Depuis z = −420, il reste à −325 —
        // largement derrière le scrim, qu'il ne peut donc pas percer.
        this._s.set(w, h, w * 0.35);
        this._m.compose(this._p, this._q, this._s);
        this.trench.setMatrixAt(n, this._m);

        const t = col.tint * (0.72 + h2 * 0.5);
        const cc = this.trench.instanceColor!;
        cc.array[n * 3] = t;
        cc.array[n * 3 + 1] = t;
        cc.array[n * 3 + 2] = t;
        n++;
      }
    }

    this.trench.instanceMatrix.needsUpdate = true;
    this.trench.instanceColor!.needsUpdate = true;
  }
}
